import type { PlatformLayout } from "./types";

/** Level 3 — The Clockwork Canopy. Multi-tier platform network. */
export const CLOCKWORK_CANOPY_PLATFORMS: PlatformLayout = {
  level: "ClockworkCanopy",
  groundTopY: 0,
  platforms: [
    // Section 1: Gear Alignment area
    // plat_s1_1 extended west (w: 5→12, center 10→7) so spawn positions
    // (x=5, x=7) land on the same platform. Same fix as L1.
    { name: "plat_s1_1", w: 12, x: 7, y: 2.0 },
    { name: "plat_s1_2", w: 4, x: 16, y: 3.0 }, // Bridge area
    { name: "plat_s1_3", w: 3, x: 12, y: 4.0 },
    // plat_s1_4 widened from w=3 (x=[18.5,21.5]) to w=5 (x=[17.5,22.5]):
    // gearB's platform is only reachable by an Overclocked Leap whose
    // 8s cooldown made each missed landing on the old 3u slab a long,
    // punishing retry loop for humans. The wider deck keeps the leap as
    // the route but forgives normal air-drift error. West edge (17.5)
    // clears plat_s1_2 (x=[14,18] top 3.2) with 2.6u of headroom.
    { name: "plat_s1_4", w: 5, x: 20, y: 6.0, route: "minna", note: "gearB deck: Basil arrives by Overclocked Leap (GearAlignment)" },
    // Section 2: Light Path area
    // #13: plat_s2_1 widened from w=6 @ center 30 (x=[27,33]) to w=8 @
    // center 31 (x=[27,35]) so basil has ≥2.5u of overlap with plat_s2_3
    // (x=[32.5,37.5]) to launch an OvLeap up to pedestal_0. The prior
    // 0.5u overlap was an unforgivably tight launch window. Basil's
    // spawn (x=30 from PUZZLE_ORIGIN) is still well inside the wider
    // footprint, and the east edge at x=35 doesn't collide with
    // plat_s2_2 (x=[36,40]).
    { name: "plat_s2_1", w: 8, x: 31, y: 2.0 },
    // plat_s2_2 widened from w=4 (x=[36, 40]) to w=8 (x=[36, 44]) so
    // it abuts plat_s2_7's west edge (x=44). Originally a 4u gap
    // separated plat_s2_2 and plat_s2_7 with the too-narrow
    // plat_s2_7_step in between, forcing a 2-jump traversal that
    // the navigator oscillates on. With the merged platform, basil
    // walks continuously from plat_s2_2 onto plat_s2_7 — clean path
    // to lens_2.
    { name: "plat_s2_2", w: 8, x: 40, y: 2.0 },
    { name: "plat_s2_3", w: 5, x: 35, y: 6.0, route: "assisted", note: "Overclocked Leap from plat_s2_1 to pedestal_0" },
    // plat_s2_4 narrowed from w=4 (x=[38, 42]) to w=2 (x=[39, 41]).
    // The original west edge at x=38 sat directly above lens_1 (at
    // x=38, y=2.5), so basil's OvLeap from lens_1 — even straight
    // up — clipped the capsule top against plat_s2_4's bottom (y=7.8)
    // at center y≈6.665, well below pedestal_1 (y=8.3). Narrowing
    // the plat to start at x=39 gives basil's launch a 1u clearance
    // (capsule east edge 38.4 < 39) so the arc reaches full apex
    // y≈21u. Pedestal_1 at (40, 8.3) is still on the narrower plat.
    { name: "plat_s2_4", w: 2, x: 40, y: 8.0, route: "assisted", note: "Overclocked Leap from lens_1 to pedestal_1" },
    { name: "plat_s2_5", w: 4, x: 45, y: 10.0, route: "assisted", note: "reached from the leap tier" },
    { name: "plat_s2_6", w: 3, x: 50, y: 10.0, route: "assisted", note: "reached from the leap tier" },
    // #14: `plat_s2_7_step` is a small step-up prop at x=42.5, y=1.0
    // that visually cues basil to jump BEFORE passing under plat_s2_7
    // (x=[44,48], bottom y=1.8). Walking east on the ground he'd
    // otherwise pass under plat_s2_7 without being able to reach lens_2
    // (on top at y=2.5). Hopping onto the step (top y=1.2) then up to
    // plat_s2_7 (top y=2.2) makes the approach from the west legible.
    { name: "plat_s2_7_step", w: 1.5, x: 42.5, y: 1.0 },
    { name: "plat_s2_7", w: 4, x: 46, y: 2.0 },
    // plat_s2_8: the purr perch. The star crystal (50, 10.3) on plat_s2_6
    // only wakes to Minna's Purr (6 u radius), and plat_s2_6 is out of
    // ordinary-jump reach. From anywhere on this ledge (top 4.2, Minna's
    // centre ≈ 4.94) she is ≤ 5.7 u from the crystal. It is a 2.0 u hop
    // from plat_s2_7 (0.5 u gap) or plat_s3_1 (2.5 u gap), so both cats
    // can climb it; only Minna's Purr does anything there. West edge 48.5
    // stays clear of Basil's lens-2 leap (from x ≤ 47.5 on plat_s2_7).
    { name: "plat_s2_8", w: 3, x: 50, y: 4.0, route: "jump", note: "purr perch under the star crystal (LightPath): Minna purrs from here" },
    // Section 3: Timed Resonance area
    { name: "plat_s3_1", w: 8, x: 58, y: 2.0 },
    { name: "plat_s3_2", w: 5, x: 67, y: 3.0 },
    { name: "plat_s3_3", w: 6, x: 74, y: 2.0 },
    // Section 4: Shadow Relay area
    // plat_s4_1 widened from w=6 → w=10 (centered at x=80 instead of
    // x=82) so that the relay plate at x=85 sits 5u east of plat west
    // edge, leaving enough runway west of the plate for Minna's
    // running-jump to plat_s4_2. The original 6u-wide plat with the
    // plate at its east edge gave Minna only ~1.5u of clear runway
    // before colliding with anchored Basil's capsule. Canon (book p.18)
    // describes the plate as "in a shadow zone" without anchoring it
    // to a specific platform geometry — so widening west is canon-soft.
    { name: "plat_s4_1", w: 10, x: 80, y: 2.0 },
    { name: "plat_s4_2", w: 10, x: 95, y: 2.0 },
    { name: "plat_s4_3", w: 5, x: 105, y: 4.0 },
    // Section 5: Orrery / Apex area
    { name: "plat_s5_1", w: 8, x: 115, y: 3.0 },
    // The sync platforms top out at y=5.0, inside Minna's normal jump
    // envelope from plat_s5_1. Thin slabs keep the underside from
    // becoming a collision ceiling during the approach.
    { name: "plat_s5_2", w: 5, h: 0.2, x: 120, y: 4.9 },
    { name: "plat_s5_3", w: 5, h: 0.2, x: 130, y: 4.9 },
    { name: "plat_s5_4", w: 4, h: 0.2, x: 135, y: 4.9 },
  ],
  extraColliders: [
    // Central orrery dais — keep it inside the gap between the sync
    // platforms so it reads as a brass focal point without becoming a low
    // collision ceiling over the approach route. It is also the stepping
    // stone across the 5u plat_s5_2 → plat_s5_3 gap.
    { name: "orreryPlatform", w: 4.2, h: 0.5, x: 125, y: 5.0 },
  ],
};
